<template>
  <EInstantAnswer
    v-if="primaryDateValid"
    :feedback-link="{
      ticketSubject: 'Search - Timezone',
      widgetType: 'timezone_rich'
    }"
    :sr-title="$t('common.search.widgets.timezone.title')"
    :trees="otherTimes.length === 0"
    class="timezone-widget"
  >
    <template #context>
      {{ $t('web.widgets.timezone.timein') }} {{ primaryLocation }}
    </template>
    <template #title>
      <time
        class="timezone__main-time-clock"
        data-test-id="primary-clock-time"
        :datetime="primary.time"
      >
        {{ primaryClockTime }}
      </time>
    </template>
    <template #subtitle>
      <time :datetime="primaryDate">
        {{ formattedPrimaryDate }}
      </time>
    </template>
    <div v-if="otherTimes.length > 0">
      <EOtherTimes :items="otherTimes" />
    </div>
  </EInstantAnswer>
</template>
<script>
import { mapState } from 'vuex';
import EInstantAnswer from '~/components/instant-answers/base';
import { resetToUTC } from '~~/common/js/universal/datetime';
import { trackImpression, PAGE_SERP, TYPE_RICH } from '~~/search/common/js/client/analytics/rich-content';
import EOtherTimes from './other-times';

const dateFormat = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  weekday: 'long',
};

const timeFormat = { hour: '2-digit', minute: '2-digit' };
export const TRACK_ID = 'timezone';

export default {
  name: 'ETimezone',
  components: {
    EInstantAnswer,
    EOtherTimes,
  },
  props: {
    primary: {
      type: Object,
      required: true,
    },
    others: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  computed: {
    ...mapState('global', ['locale']),
    primaryDate() {
      return resetToUTC(new Date(this.primary.time));
    },
    primaryDateValid() {
      return !isNaN(this.primaryDate);
    },
    primaryClockTime() {
      return this.getClockTime(this.primaryDate);
    },
    formattedPrimaryDate() {
      const utc = this.attributeOffset(this.primary.utcOffset);
      const localizedDate = this.getLocalizedDate(this.primaryDate);
      return `${localizedDate[0].toUpperCase() + localizedDate.slice(1)} (${utc})`;
    },
    primaryLocation() {
      return this.primary.location;
    },
    otherTimes() {
      // checks if utc is valid, otherwise remove entry from otherTimes
      return this.others.flatMap((entry) => {
        const formatted = this.formatEntry(entry);
        return typeof formatted.utc === 'undefined' ? [] : formatted;
      });
    },
  },
  mounted() {
    trackImpression({
      name: TRACK_ID,
      page: PAGE_SERP,
      type: TYPE_RICH,
      once: false,
    });
  },
  methods: {
    getClockTime(date) {
      return date.toLocaleTimeString(this.locale, timeFormat);
    },
    getLocalizedDate(date) {
      return date.toLocaleDateString(this.locale, dateFormat);
    },
    formatEntry(entry) {
      const { location, time, utcOffset } = entry;
      const utc = this.attributeOffset(utcOffset);
      const clockTime = this.getClockTime(resetToUTC(new Date(time)));
      return { clockTime, location, utc, time };
    },
    attributeOffset(offset) {
      const utcRegex = /UTC(\+|-)[0-1]*[0-9]{1}(:[0-9]{1,2})*/g;
      return utcRegex.test(offset) ? offset : undefined;
    },
  },
};
</script>
<style lang="scss" scoped>
.timezone__main-time-clock {
  display: block;
}
</style>
